import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SSHInstanceStatus, SSHInstanceStatusLabel, InstanceTag, InstanceTagLabel } from '@/service/gpu-instance/type';
import { Terminal, StopCircle, Loader2, Pencil, Check, X, Zap, Server, Target, HeartPulse, AudioWaveform, Sparkles, Pause, Play, Trash2, History } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useListSSHInstances, useTerminateSSHInstance, useStopSSHInstance, useResumeSSHInstance } from '@/service/gpu-instance/hook';
import { renameSSHInstance } from '@/service/gpu-instance';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { useDemoMode } from '@/context/DemoModeContext';
import {
  VLLMDetailPanel,
  TritonDetailPanel,
  TAODetailPanel,
  MONAIDetailPanel,
  NeMoDetailPanel,
  Float16TrainingPanel,
} from '../../components/detail-panels';

const formatRunningTime = (startTime: number | null, endTime: number | null) => {
  if (!startTime) return '-';

  const start = startTime * 1000;
  const end = endTime ? endTime * 1000 : Date.now();
  const diff = end - start;

  if (diff < 0) return '-';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${days.toString().padStart(2, '0')}d ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
};

const getStatusBadgeVariant = (status: SSHInstanceStatus) => {
  switch (status) {
    case SSHInstanceStatus.Running:
    case SSHInstanceStatus.SSHReady:
      return 'success';
    case SSHInstanceStatus.Pending:
      return 'warning';
    case SSHInstanceStatus.Stopped:
      return 'outline';
    case SSHInstanceStatus.Failed:
      return 'destructive';
    case SSHInstanceStatus.Completed:
    case SSHInstanceStatus.Cancelled:
    case SSHInstanceStatus.Terminated:
      return 'secondary';
    default:
      return 'default';
  }
};

const getTagBadgeClass = (tag: InstanceTag) => {
  switch (tag) {
    case 'vllm':
    case 'ssh-vllm':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    case 'triton':
    case 'ssh-triton':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/30';
    case 'tao':
    case 'ssh-tao':
      return 'bg-green-500/10 text-green-600 border-green-500/30';
    case 'monai':
    case 'ssh-monai':
      return 'bg-red-500/10 text-red-600 border-red-500/30';
    case 'nemo':
    case 'ssh-nemo':
      return 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30';
    case 'float16-training':
    case 'ssh-float16':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/30';
    case 'ocr':
    case 'ssh-ocr':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/30';
    case 'ssh':
      return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
  }
};

const getTagIcon = (tag: InstanceTag) => {
  switch (tag) {
    case 'vllm':
    case 'ssh-vllm':
      return <Zap className="h-4 w-4" />;
    case 'triton':
    case 'ssh-triton':
      return <Server className="h-4 w-4" />;
    case 'tao':
    case 'ssh-tao':
      return <Target className="h-4 w-4" />;
    case 'monai':
    case 'ssh-monai':
      return <HeartPulse className="h-4 w-4" />;
    case 'nemo':
    case 'ssh-nemo':
      return <AudioWaveform className="h-4 w-4" />;
    case 'float16-training':
    case 'ssh-float16':
      return <Sparkles className="h-4 w-4" />;
    case 'ssh':
      return <Terminal className="h-4 w-4" />;
    default:
      return null;
  }
};

const GPUInstanceDetailOverview = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data: instances, isLoading, isFetching, refetch } = useListSSHInstances();
  const { token } = useGPUInstanceStore();
  const terminateMutation = useTerminateSSHInstance();
  const stopMutation = useStopSSHInstance();
  const resumeMutation = useResumeSSHInstance();
  const { isDemoMode } = useDemoMode();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [runningTime, setRunningTime] = useState('-');

  // Find the instance and resolve to the latest in the parent_task_id family
  const instance = (() => {
    if (!instances || !instanceId) return undefined;

    const directMatch = instances.find((i) => i.task_id === instanceId);
    if (!directMatch) return undefined;

    // Find all instances in the same parent_task_id family
    const parentId = directMatch.parent_task_id || directMatch.task_id;
    const familyInstances = instances.filter(
      (i) => i.parent_task_id === parentId || i.task_id === parentId
    );

    if (familyInstances.length <= 1) return directMatch;

    // Return the best instance:
    // 1. If there's a Running/SSHReady instance, show that
    // 2. If there's a Pending instance, show that
    // 3. Otherwise, show the most recently started instance
    const runningInstance = familyInstances.find(
      (i) => i.status === SSHInstanceStatus.Running || i.status === SSHInstanceStatus.SSHReady
    );
    if (runningInstance) return runningInstance;

    const pendingInstance = familyInstances.find(
      (i) => i.status === SSHInstanceStatus.Pending
    );
    if (pendingInstance) return pendingInstance;

    // No active instance, return the most recent one by start_time, then end_time
    return familyInstances.sort((a, b) => {
      const startDiff = (b.start_time || 0) - (a.start_time || 0);
      if (startDiff !== 0) return startDiff;
      return (b.end_time || 0) - (a.end_time || 0);
    })[0];
  })();

  // Get all instances in the same parent_task_id family for history display
  const instanceHistory = useMemo(() => {
    if (!instances || !instance) return [];

    const parentId = instance.parent_task_id || instance.task_id;
    const familyInstances = instances.filter(
      (i) => i.parent_task_id === parentId || i.task_id === parentId
    );

    // Sort by created_at descending (newest first)
    return familyInstances.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
  }, [instances, instance]);

  useEffect(() => {
    if (!instance?.start_time) {
      setRunningTime('-');
      return;
    }

    const isRunning = instance.status === SSHInstanceStatus.SSHReady || instance.status === SSHInstanceStatus.Running;

    // Calculate running time: for running instances, use current time; otherwise use end_time
    const calculateTime = () => {
      if (isRunning) {
        // Instance is running (ssh_ready or running), calculate from start_time to now
        return formatRunningTime(instance.start_time, null);
      }
      // Instance is not running, use the recorded end_time
      return formatRunningTime(instance.start_time, instance.end_time);
    };

    setRunningTime(calculateTime());

    // Only set up interval if instance is running (ssh_ready or running status)
    if (isRunning) {
      const interval = setInterval(() => {
        setRunningTime(calculateTime());
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [instance?.start_time, instance?.end_time, instance?.status]);

  const handleTerminate = async () => {
    if (!instance?.task_id) return;
    try {
      await terminateMutation.mutateAsync(instance.task_id);
      toast.success('Instance terminated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to terminate instance');
    }
  };

  const handleStop = async () => {
    if (!instance?.task_id) return;
    try {
      await stopMutation.mutateAsync(instance.task_id);
      toast.success('Instance stopped successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to stop instance');
    }
  };

  const handleResume = async () => {
    if (!instance?.task_id) return;
    try {
      await resumeMutation.mutateAsync(instance.task_id);
      toast.success('Instance resumed successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to resume instance');
    }
  };

  const handleStartEdit = () => {
    setNewName(instance?.project_name || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewName('');
  };

  const handleRename = async () => {
    if (!instance?.project_id || !token || !newName.trim()) return;
    setIsRenaming(true);
    try {
      await renameSSHInstance(instance.project_id, newName.trim(), token);
      toast.success('Project renamed');
      refetch();
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to rename project');
    } finally {
      setIsRenaming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container py-8 flex justify-center">
        <Loader2 className="size-8 animate-spin" />
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertDescription>
            Instance not found. It may have been terminated or the ID is invalid.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Render tag-specific panel based on instance tag
  const renderTagSpecificPanel = () => {
    if (!instance.instance_tag || !instance.task_id) return null;

    const tag = instance.instance_tag;

    // vLLM panel is available in both demo and production modes
    if (tag === 'vllm' || tag === 'ssh-vllm') {
      return <VLLMDetailPanel taskId={instance.task_id} />;
    }

    // Other panels are demo-only for now
    if (!isDemoMode) return null;

    switch (tag) {
      case 'triton':
      case 'ssh-triton':
        return <TritonDetailPanel taskId={instance.task_id} />;
      case 'tao':
      case 'ssh-tao':
        return <TAODetailPanel taskId={instance.task_id} />;
      case 'monai':
      case 'ssh-monai':
        return <MONAIDetailPanel taskId={instance.task_id} />;
      case 'nemo':
      case 'ssh-nemo':
        return <NeMoDetailPanel taskId={instance.task_id} />;
      case 'float16-training':
      case 'ssh-float16':
        return <Float16TrainingPanel taskId={instance.task_id} />;
      default:
        return null;
    }
  };

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="text-xl font-bold h-9 w-64"
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={handleRename} disabled={isRenaming}>
                {isRenaming ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit} disabled={isRenaming}>
                <X className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{instance.project_name}</h1>
              {instance.instance_tag && (
                <Badge variant="outline" className={`flex items-center gap-1 ${getTagBadgeClass(instance.instance_tag)}`}>
                  {getTagIcon(instance.instance_tag)}
                  {InstanceTagLabel[instance.instance_tag]}
                </Badge>
              )}
              <Button size="sm" variant="ghost" onClick={handleStartEdit}>
                <Pencil className="size-4" />
              </Button>
            </div>
          )}
          <p className="text-muted-foreground mt-1 font-mono text-sm">
            {instance.parent_task_id || instance.task_id}
          </p>
        </div>
        <div className="flex gap-2">
          {(() => {
            const isAnyActionPending = stopMutation.isPending || resumeMutation.isPending || terminateMutation.isPending || isFetching;

            return (
              <>
                {/* Stop button - shown when instance is running */}
                {(instance.status === SSHInstanceStatus.Running || instance.status === SSHInstanceStatus.SSHReady) && (
                  <Button variant="outline" onClick={handleStop} disabled={isAnyActionPending}>
                    {stopMutation.isPending ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Pause className="size-4 mr-2" />
                    )}
                    {stopMutation.isPending ? 'Stopping...' : 'Stop'}
                  </Button>
                )}

                {/* Resume button - shown when instance is stopped */}
                {instance.status === SSHInstanceStatus.Stopped && (
                  <Button variant="outline" onClick={handleResume} disabled={isAnyActionPending}>
                    {resumeMutation.isPending ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="size-4 mr-2" />
                    )}
                    {resumeMutation.isPending ? 'Resuming...' : 'Resume'}
                  </Button>
                )}

                {/* Terminate button - shown for running or stopped instances */}
                {(instance.status === SSHInstanceStatus.Running ||
                  instance.status === SSHInstanceStatus.SSHReady ||
                  instance.status === SSHInstanceStatus.Stopped) && (
                  <Button variant="destructive" onClick={handleTerminate} disabled={isAnyActionPending}>
                    {terminateMutation.isPending ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="size-4 mr-2" />
                    )}
                    {terminateMutation.isPending ? 'Terminating...' : 'Terminate'}
                  </Button>
                )}
              </>
            );
          })()}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Instance Details */}
        <Card>
          <CardHeader>
            <CardTitle>Instance Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={getStatusBadgeVariant(instance.status) as any}>
                {SSHInstanceStatusLabel[instance.status] || instance.status}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">GPU Type</span>
              <span className="font-medium">{instance.gpu_type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">IP Address</span>
              <span className="font-medium font-mono">{instance.instance_ip || '-'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Created</span>
              <span className="font-medium">
                {instance.created_at ? new Date(instance.created_at * 1000).toLocaleString() : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Running Time</span>
              <span className="font-medium font-mono">{runningTime}</span>
            </div>
          </CardContent>
        </Card>

        {/* SSH Connection */}
        {(instance.status === SSHInstanceStatus.Running || instance.status === SSHInstanceStatus.SSHReady) && instance.instance_ip ? (
          <Card className="border-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <Terminal className="size-5" />
                SSH Connection
              </CardTitle>
              <CardDescription>
                Connect to your instance via SSH
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Host</span>
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  slintprod-1.float16.cloud
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Port</span>
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  2222
                </code>
              </div>

              <Alert>
                <AlertDescription>
                  Use the SSH key provided during instance creation to connect.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="size-5" />
                SSH Connection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                SSH connection will be available when the instance is running.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tag-specific detail panel */}
      {renderTagSpecificPanel()}

      {/* Instance History - shown when there is at least 1 instance (at the bottom) */}
      {instanceHistory.length >= 1 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="size-5" />
              Instance History
            </CardTitle>
            <CardDescription>
              Stop/Resume history for this instance ({instanceHistory.length} total)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Task ID</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">IP Address</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Created</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Started</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Ended</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {instanceHistory.map((historyInstance) => {
                    const duration = historyInstance.start_time
                      ? formatRunningTime(historyInstance.start_time, historyInstance.end_time)
                      : '-';
                    const isCurrent = historyInstance.task_id === instance.task_id;

                    return (
                      <tr
                        key={historyInstance.task_id}
                        className={`border-b last:border-0 ${
                          isCurrent ? 'bg-primary/5' : 'hover:bg-muted/30'
                        }`}
                      >
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                              {historyInstance.task_id}
                            </code>
                            {isCurrent && (
                              <Badge variant="default" className="text-xs">Current</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <Badge variant={getStatusBadgeVariant(historyInstance.status) as any}>
                            {SSHInstanceStatusLabel[historyInstance.status] || historyInstance.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-3">
                          <code className="text-xs font-mono">
                            {historyInstance.instance_ip || '-'}
                          </code>
                        </td>
                        <td className="py-3 px-3 text-muted-foreground">
                          {historyInstance.created_at
                            ? new Date(historyInstance.created_at * 1000).toLocaleString()
                            : '-'}
                        </td>
                        <td className="py-3 px-3 text-muted-foreground">
                          {historyInstance.start_time
                            ? new Date(historyInstance.start_time * 1000).toLocaleString()
                            : '-'}
                        </td>
                        <td className="py-3 px-3 text-muted-foreground">
                          {historyInstance.end_time
                            ? new Date(historyInstance.end_time * 1000).toLocaleString()
                            : '-'}
                        </td>
                        <td className="py-3 px-3">
                          <code className="text-xs font-mono">{duration}</code>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GPUInstanceDetailOverview;
